import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Import the existing Firebase app instance
import app from '../firebase/config';

// Use the existing Firebase app
const auth = getAuth(app);
const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();

// Function to check if a user is authorized
const checkAuthorization = httpsCallable(functions, 'checkAuthorization');

// Create auth context
const AuthContext = createContext();

// Custom hook to use auth context
export function useAuth() {
  return useContext(AuthContext);
}

// Auth provider component
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sign up with email and password
  async function signup(email, password) {
    try {
      setError('');
      // Only allow abdirisak.ahmed@gmail.com to sign up during development
      if (email !== 'abdirisak.ahmed@gmail.com') {
        setError('Registration is currently restricted to authorized developers only.');
        throw new Error('Registration is currently restricted to authorized developers only.');
      }
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Sign in with email and password
  async function login(email, password) {
    try {
      setError('');
      // Only allow abdirisak.ahmed@gmail.com to login during development
      if (email !== 'abdirisak.ahmed@gmail.com') {
        setError('Access to FixMate AI MVP is currently restricted to authorized users only.');
        throw new Error('Access to FixMate AI MVP is currently restricted to authorized users only.');
      }
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Sign in with Google
  async function loginWithGoogle() {
    try {
      setError('');
      const result = await signInWithPopup(auth, googleProvider);
      
      // Check if the Google account email is authorized
      if (result.user.email !== 'abdirisak.ahmed@gmail.com') {
        setError('Access to FixMate AI MVP is currently restricted to authorized users only.');
        // Sign out unauthorized users immediately
        await signOut(auth);
        throw new Error('Access to FixMate AI MVP is currently restricted to authorized users only.');
      }
      
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Log out
  async function logout() {
    try {
      setError('');
      await signOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Reset password
  async function resetPassword(email) {
    try {
      setError('');
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Set up auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Only check authorization for non-development email addresses
          if (user.email === 'abdirisak.ahmed@gmail.com') {
            // This is the authorized developer, set them as current user without additional checks
            setCurrentUser(user);
          } else {
            // For other users, check authorization with Firebase function
            try {
              // Wait for the token to be refreshed before checking authorization
              await user.getIdToken(true);
              // Now check authorization
              await checkAuthorization();
              setCurrentUser(user);
            } catch (authError) {
              console.error('Authorization check failed:', authError);
              setError('Access restricted to authorized users only.');
              // Sign out unauthorized users
              await signOut(auth);
              setCurrentUser(null);
            }
          }
        } catch (error) {
          console.error('Error in auth state listener:', error);
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Context value
  const value = {
    currentUser,
    loading,
    error,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
